/**
 * The first-login "Verify your mobile number" pop-up.
 *
 * Shown once per account to customers whose mobile is not verified -- the
 * server says who is due (`userData.mobileVerifyPrompt`) and is told as soon as
 * it is shown, so the app will not ask again either. After that the Account
 * page offers the same pop-up next to the phone number.
 *
 * Never over checkout or a payment result: someone paying should not be
 * interrupted. It waits for the next page instead.
 */

import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { AppDispatch, RootState } from 'reduxStore/store';
import { markMobileVerifyPrompted } from 'reduxStore/store/auth';
import { markMobileVerifyPromptShown } from 'service/authApi';
import MobileVerifyModal, { verifiableAccountMobile } from './MobileVerifyModal';

const NOT_ON = ['/checkout', '/cart', '/page/new-order-success', '/page/order-failure', '/login', '/signup'];

const MobileVerifyPrompt: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { pathname } = useLocation();
    const authData = useSelector((state: RootState) => state?.auth);

    // Held here once open: the flag is cleared as it opens, and the pop-up
    // must stay up while the customer answers. `open` is the state, because the
    // number may legitimately be absent.
    const [open, setOpen] = useState(false);

    const accessToken: string | undefined = authData?.accessToken || undefined;
    const userData = authData?.userData;
    const accountMobile = verifiableAccountMobile(userData?.customer);
    // A number on file is NOT required (changed 18 Oct). Account mobiles are
    // cleared at go-live, so requiring one here would have hidden this pop-up
    // from every customer it exists for -- silently, since `due` would simply
    // be false. Whether to ask is the server's decision alone.
    const due = Boolean(accessToken && userData?.mobileVerifyPrompt && !userData?.mobileVerified);
    const blocked = NOT_ON.some((path) => pathname.startsWith(path));

    useEffect(() => {
        if (!due || blocked || open || !accessToken) return;
        setOpen(true);
        dispatch(markMobileVerifyPrompted());
        // Best effort: a failure costs one repeat of the pop-up after the next sign-in.
        markMobileVerifyPromptShown(accessToken).catch(() => undefined);
    }, [due, blocked, open, accessToken, dispatch]);

    if (!open) return null;

    return <MobileVerifyModal open mobile={accountMobile} accessToken={accessToken} onClose={() => setOpen(false)} />;
};

export default MobileVerifyPrompt;
